import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { authApi } from '@/services/api';
import { getTrustedDeviceToken, clearTrustedDeviceToken } from '@/services/trustedDevice';
import { useAuthStore } from '@/stores/authStore';
import {
  TruckIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

const loginSchema = z.object({
  email: z.string().email('البريد الإلكتروني غير صالح'),
  password: z.string().min(6, 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      // If this browser was previously marked trusted for this account, send
      // the stored token so the server can skip the OTP step.
      const deviceToken = getTrustedDeviceToken(data.email);
      const response = await authApi.login(data.email, data.password, deviceToken || undefined);
      const result = response.data.data;

      // Two-step login when the backend requires OTP: it returns
      //   { otpRequired: true, loginSessionToken, channels, ... }
      // We carry the session over to the OTP screen via router state
      // (not URL params — the token shouldn't leak into history/logs).
      if (result?.otpRequired && result?.loginSessionToken) {
        // We offered a token and still got asked for a code: it was revoked or
        // has expired server-side. Drop it so it isn't retried every login.
        if (deviceToken) clearTrustedDeviceToken(data.email);
        navigate('/otp-verify', {
          state: {
            loginSessionToken: result.loginSessionToken,
            channels: result.channels || [],
            expiresInSec: result.loginSessionExpiresInSec || 300,
            email: data.email,
          },
        });
        return;
      }

      // A newly created account: the server withheld every token and handed
      // back a one-shot change token instead. Without this branch the flow
      // fell through to "unexpected response" and the account could never
      // be used at all.
      if (result?.mustChangePassword && result?.passwordChangeToken) {
        navigate('/change-password', {
          state: {
            passwordChangeToken: result.passwordChangeToken,
            email: result.email || data.email,
            // Carried in router state (in memory, never the URL) so the user
            // does not retype what they just entered.
            oldPassword: data.password,
          },
        });
        return;
      }

      // Single-step login (no OTP): server returns tokens directly.
      if (result?.tokens && result?.user) {
        setAuth(result.user, result.tokens.accessToken, result.tokens.refreshToken);
        toast.success('تم تسجيل الدخول بنجاح');
        navigate('/');
        return;
      }

      // 200 OK with an unrecognised shape — surface a useful error
      // instead of crashing on a destructure.
      throw new Error('استجابة الخادم غير متوقعة');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'فشل تسجيل الدخول';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary mb-4">
              <TruckIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">نظام تتبع الإسعاف</h1>
            <p className="text-gray-500 mt-2">مرحباً بك، قم بتسجيل الدخول للمتابعة</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <div>
              <label className="label">البريد الإلكتروني</label>
              <div className="relative">
                <EnvelopeIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  {...register('email')}
                  className={`input pr-10 ${errors.email ? 'input-error' : ''}`}
                  placeholder="example@ministry.gov"
                  dir="ltr"
                />
              </div>
              {errors.email && (
                <p className="error-message">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="label">كلمة المرور</label>
              <div className="relative">
                <LockClosedIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className={`input pr-10 pl-10 ${errors.password ? 'input-error' : ''}`}
                  placeholder="••••••••"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="error-message">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600">تذكرني</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                نسيت كلمة المرور؟
              </Link>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                'تسجيل الدخول'
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-8">
            © {new Date().getFullYear()} وزارة الصحة - جميع الحقوق محفوظة
          </p>
        </div>
      </div>

      {/* Right side - Image/Branding */}
      <div className="hidden lg:flex lg:flex-1 gradient-primary items-center justify-center p-12">
        <div className="max-w-lg text-center text-white">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-white/10 flex items-center justify-center">
            <TruckIcon className="w-16 h-16" />
          </div>
          <h2 className="text-3xl font-bold mb-4">
            نظام تتبع وإدارة سيارات الإسعاف
          </h2>
          <p className="text-lg text-white/80 leading-relaxed">
            منصة متكاملة لتتبع ومراقبة أسطول سيارات الإسعاف في الوقت الفعلي،
            مع إمكانية إدارة المهام والتنبيهات وإعداد التقارير.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">500+</div>
              <div className="text-sm text-white/70">مركبة</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">50+</div>
              <div className="text-sm text-white/70">مستشفى</div>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <div className="text-3xl font-bold">24/7</div>
              <div className="text-sm text-white/70">مراقبة</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
